/**
 * Single import point for the two barcode modules, both of which are CommonJS and are
 * deliberately NOT rewritten for the bundler.
 *
 *   ../../vendor/impb.js  — barcode DATA: constructs, field widths, MOD 10, human-readable
 *                            suppression. BYTE-IDENTICAL to shared/impb/impb.js (71/71
 *                            conformance tests). build.mjs refuses to build if the SHA-256
 *                            of the two files differs, so the browser and the Lambdas cannot
 *                            disagree about what a valid PIC is.
 *   ../../barcode.js      — barcode SYMBOLOGY: Code 128 patterns, MOD 103, module stream and
 *                            the canvas renderer with whole-device-pixel bar snapping
 *                            (39/39 tests, `node web/barcode.test.js`).
 *
 * Both files stay at web/ root rather than moving under src/ so their existing Node test
 * suites keep running unchanged with no dependencies — `node web/barcode.test.js` works on a
 * bare checkout. esbuild interops with CommonJS, so no shim is needed here; the old
 * `window.module = {exports:{}}` hack in the vanilla index.html is gone.
 */

import Impb from '../../vendor/impb.js';
import Barcode from '../../barcode.js';

export { Impb, Barcode };

/** SHA-256 that build.mjs actually verified for shared/impb/impb.js. */
export const IMPB_SHA256 = typeof __IMPB_SHA256__ === 'string' ? __IMPB_SHA256__ : 'unverified';
export const BUILT_AT = typeof __BUILT_AT__ === 'string' ? __BUILT_AT__ : 'unknown';

/**
 * Split an encoded digit string into fields for a KNOWN construct, verifying MOD 10.
 *
 * ⚠️ WHY THIS EXISTS INSTEAD OF JUST CALLING parseBarcode():
 * C01, C03 and C04 have BYTE-IDENTICAL 22-digit PIC signatures (channel AI 92, 9-digit MID,
 * 7-digit serial). They differ ONLY in the routing element that PRECEDES the PIC — and C04 has
 * no routing element at all. So a bare 22-digit PIC genuinely cannot identify its own
 * construct, and impb.parseBarcode() resolves the tie by returning the FIRST match, which is
 * C01. That is correct for a lone PIC with nothing else to go on, but it would mislabel every
 * C04 in this UI as a C01.
 *
 * The API declares the construct (per the contract), so when we have that declaration we
 * HONOUR it and verify the digits against it — including the MOD 10 check digit. Correctness
 * is not traded for a nicer label; the ambiguity is resolved by information, not by guessing.
 */
export function fieldsForConstruct(encoded, constructId) {
  const spec = Impb.CONSTRUCTS[constructId];
  if (!spec) throw new Error(`unknown construct ${constructId}`);
  if (encoded.length !== spec.totalDigits) {
    throw new Error(`construct ${constructId} requires ${spec.totalDigits} digits, got ${encoded.length}`);
  }
  let o = 0;
  let routingAI = null;
  let zip = null;
  if (spec.hasRouting) {
    routingAI = encoded.slice(0, 3);
    if (routingAI !== Impb.ROUTING_AI) {
      throw new Error(`construct ${constructId} must start with ${Impb.ROUTING_AI}, got ${routingAI}`);
    }
    o = 3;
    zip = encoded.slice(o, o + spec.zipLength);
    o += spec.zipLength;
  }
  const pic = encoded.slice(o);
  const channelAI = pic.slice(0, 2);
  if (channelAI !== spec.channelAI) {
    throw new Error(`construct ${constructId} expects channel AI ${spec.channelAI}, got ${channelAI}`);
  }
  let p = 2;
  const serviceTypeCode = pic.slice(p, (p += Impb.STC_LENGTH));
  const mailerId = pic.slice(p, (p += spec.midLength));
  const serial = pic.slice(p, (p += spec.serialLength));
  const checkDigit = pic.slice(p, p + 1);

  // The MOD 10 data check digit is the part that actually proves integrity.
  const expected = String(Impb.mod10CheckDigit(pic.slice(0, pic.length - 1)));
  if (checkDigit !== expected) {
    throw new Error(`MOD 10 check digit mismatch: found ${checkDigit}, expected ${expected}`);
  }
  return {
    construct: constructId,
    encoded,
    pic,
    humanReadable: Impb.groupDigits(pic),
    fields: { routingAI, zip, channelAI, serviceTypeCode, mailerId, serial, checkDigit },
    gs1ElementString: null,
  };
}

/**
 * Rebuild an impb barcode object from an API response item.
 *
 * The contract returns pic/encoded/humanReadable/construct but not the parsed field breakdown.
 * We reconstruct the fields and INDEPENDENTLY verify the MOD 10 check digit. A failure — or a
 * disagreement between the API's own humanReadable and the spec grouping rule — is surfaced
 * loudly, because a service disagreeing with the shared module is exactly the integration bug
 * this demo must not paper over.
 */
export function barcodeFromApiItem(item) {
  const encoded = String(item.encoded || item.pic || '');
  if (!/^[0-9]+$/.test(encoded)) {
    const err = new Error(`API returned a non-numeric barcode: ${JSON.stringify(item.encoded || item.pic)}`);
    err.contractMismatch = true;
    throw err;
  }

  let b;
  if (item.construct && Impb.CONSTRUCTS[item.construct]) {
    try {
      b = fieldsForConstruct(encoded, item.construct);
    } catch (err) {
      const e = new Error(
        `API declared construct ${item.construct} but the digits do not conform: ${err.message}. encoded=${encoded}`
      );
      e.contractMismatch = true;
      throw e;
    }
  } else {
    // No declaration — fall back to inference, and flag that it IS an inference.
    const parsed = Impb.parseBarcode(encoded);
    if (!parsed.valid) {
      const err = new Error(`API returned a PIC that shared/impb cannot validate: ${parsed.reason}. encoded=${encoded}`);
      err.contractMismatch = true;
      throw err;
    }
    b = { ...parsed.parsed, gs1ElementString: null, inferredConstruct: true };
  }

  const expectedHr = Impb.groupDigits(b.pic);
  b.humanReadable = expectedHr;
  b.hrMismatch =
    item.humanReadable && item.humanReadable !== expectedHr
      ? { api: item.humanReadable, expected: expectedHr }
      : null;
  return b;
}

/**
 * Constructs whose PIC portion is indistinguishable from `constructId`'s, so a bare PIC (no
 * routing element, no declared construct) cannot identify itself. Used to caption the ledger
 * lookup honestly instead of presenting an inference as a fact.
 */
export function ambiguousWith(constructId) {
  const spec = Impb.CONSTRUCTS[constructId];
  if (!spec) return [];
  return Object.values(Impb.CONSTRUCTS)
    .filter(
      (c) =>
        c.id !== constructId &&
        c.channelAI === spec.channelAI &&
        c.midLength === spec.midLength &&
        c.serialLength === spec.serialLength
    )
    .map((c) => c.id);
}
